import { CalendarDay } from './interfaces/calendar-day';
import { AjaxSettings } from './interfaces/ajax-settings';
import { AjaxStatus } from './interfaces/AjaxStatus';
import {
  DownloadFileMediaType,
  DownloadFileParams,
  DownloadFileCharset,
  TypeExtensionMap } from './interfaces/download-file-params';
import { sanitizeHtmlString } from './pds-xss';
import { StringUtil, EmailUtil, NumberUtil } from 'common-utils';
import { pdsLocalStorage } from './pds-local-storage';
import { StringBoolean } from './pds-widget';
import { BoundingBox } from './interfaces/bounding-box';
import { Offset } from './interfaces/offset';
import { Subject, Observable } from 'rxjs';

declare let unescape;
declare let global;

/**
 * This function works just like Object.getOwnPropertyDescriptor but it gets the property descriptor
 * even if it's inherited. It is defined here to keep it private.
 * @param object - The object to get the property off of.
 * @param prop - The property to get a descriptor for.
 * @returns {PropertyDescriptor} The property descriptor of the property.
 */
function getPropertyDescriptor(object: any, prop: string): PropertyDescriptor {
  let objectDescriptor: PropertyDescriptor | undefined = Object.getOwnPropertyDescriptor(object, prop);
  if (objectDescriptor == null) {
    return getPropertyDescriptor(Object.getPrototypeOf(object), prop);
  } else {
    return objectDescriptor;
  }
}

/* Creating a placeholder div element to be used by strip HTML utility function.
 Declared it outside export as we only need one instance of this element.*/
let placeholderDivToStripHTML = document.createElement('div');
placeholderDivToStripHTML.setAttribute('id', 'divToStripHTML_NotMeantForDOM');

let ajaxStatusStream: Subject<AjaxStatus> = new Subject<AjaxStatus>();
let ajaxActiveRequestsCount = 0;

export let pdsUtils = {
  window: window, //using this so it can be mocked for unit tests.
  pds: undefined,
  ESCAPE_USER_INPUT_REGEX: /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g,
  ESCAPE_EXCLUDE_CHARS_REGEX: StringUtil.ESCAPE_EXCLUDE_CHARS_REGEX,
  TIME_STRING_AM_PM_SPACING_REGEX: /(\d)(pm|am)/i,
  sanitizeHtmlString: sanitizeHtmlString,
  TAB_ELEMENTS: [
    'input',
    'select',
    'a[href]',
    'textarea',
    'button',
    '[tabindex]'
  ].map((selector: string) => selector + ':not([disabled])'),
  markedElements: [],

  /**
   * This function strips HTML tags from text.
   * @param value - The string to strip HTML tags from.
   * @return Plain text
   */
    stripHtml(value: string | undefined): string | undefined {
    if (value == null) {
      return '';
    }
    placeholderDivToStripHTML.innerHTML = value;
    return placeholderDivToStripHTML.textContent || placeholderDivToStripHTML.innerText;
  },

  escapeUserInput: function (userInput: string): string {
    if (userInput == null) {
      return userInput;
    }
    return userInput.replace(this.ESCAPE_USER_INPUT_REGEX, '\\$&');
  },

  /** This function is deprecated, please use the pds-local-storage instead */
  getPersistedBoolean: function (key: string, defaultResult = false): boolean {
    return pdsLocalStorage.getPersistedBoolean(key, defaultResult);
  },

  /**
   * This function copies text to the user's clipboard.
   * @param text - The string to copy to user's clipboard.
   * @param returnFocusHandler - callback to handle returning focus to correct element
   */
  copyTextToClipboard(text: string, returnFocusId: string): void {
    let textArea = document.createElement('textarea');
    textArea.setAttribute('id', 'textToCopy');
    textArea.style.display = 'hidden';
    textArea.value = text;
    document.body.appendChild(textArea);

    (<any>textArea).contentEditable = true;
    textArea.readOnly = false;

    if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
      //mac way
      let range: Range = document.createRange();
      range.selectNodeContents(textArea);
      let s: any = window.getSelection();
      s.removeAllRanges();
      s.addRange(range);
      textArea.setSelectionRange(0, text.length);
    } else {
      //Everything else way.
      textArea.select();  //pc way
    }

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Unable to copy');
    }

    // 8) remove the old element and put the focus back.
    document.body.removeChild(textArea);

    let returnFocusElement = document.getElementById(returnFocusId);
    if (returnFocusElement != null) {
      returnFocusElement.focus();
    }
  },

  /**
   * This function appends
   * to a stack of elements that can be focused upon
   * after a certain operation is done.
   * @param focusedElement
   */

    markFocusableElement(elementToMark: HTMLElement): void {
    if (elementToMark != null && typeof elementToMark.focus === 'function') {
      this.markedElements.push(elementToMark);
    }
  },

  /**
   * Gets the list of marked elements
   */

    getListOfMarkedElements(): any {
    return this.markedElements;
  },

  /**
   * This function gets the last marked element in the stack of elements.
   * if there are no elements that have been marked, return the current activeElement
   * if there is an element that has been marked, return the last marked element
   * @return The last element that has been marked.
   */

    getLastMarkedElement(): HTMLElement | undefined {
    let lengthOfMarkedElements = this.markedElements.length;
    if (lengthOfMarkedElements > 0) {
      return this.markedElements.pop();
    } else {
      return undefined;
    }
  },

  /**
   * This function allows the user to peek into the stack of last marked elements to see whats inside
   */

    peekLastMarkedElement(): HTMLElement {
    return this.markedElements.length > 0 ? this.markedElements[this.markedElements.length - 1] : undefined;
  },

  /**
   * finds the last element within an HTMLElement
   * @param myHTMLElement
   */
    findLastNode(myHTMLElement: HTMLElement): HTMLElement {
    let myElements = myHTMLElement.querySelectorAll(':last-child');
    return <HTMLElement>myElements[myElements.length - 1];
  },

  /**
   * Recursively finds the first element within the dom tree
   * @param myHTMLElement
   */

    findFirstNode(myHTMLElement: HTMLElement): HTMLElement {
    if (myHTMLElement.childElementCount < 1) { // base case
      return myHTMLElement;
    } else {
      return this.findFirstNode(<HTMLElement>myHTMLElement.firstElementChild);
    }
  },

  /**
   * Within an html element, this function returns all the tabbale elements
   * @param element
   */

    getAllTabbableElements(element: HTMLElement | undefined | null): any {
    if (element != null) {
      return element.querySelectorAll(this.TAB_ELEMENTS);
    }
  },

  /**
   * given an index and a list of tabbable elements, this function returns an html element at the index
   * so long that the index is less than or equal to the index of the last element available.
   * @param listOfTabbableElements - a list of tabbable elements
   * @param index - index of anywhere in the list
   */
    getTabbableElementAtIndex(listOfTabbableElements: any, index: number): any {
    if (listOfTabbableElements != null && index != null) {
      if (index <= listOfTabbableElements.length - 1) {
        return listOfTabbableElements[index];
      }
    }
  },

  /**
   * given an element reference and a list of tabbable elements,
   * this finds the next tabbable element with respect to the element reference.
   *
   * This function does NOT wrap back to the start if you pick the last element of the array.
   * It simply returns undefined
   * @param listOfTabbableElements
   * @param elementReference
   */

    getNextTabbableElement(listOfTabbableElements: any, elementReference: any): any {
    if (listOfTabbableElements != null && elementReference != null) {
      let index = listOfTabbableElements.indexOf(elementReference);
      if (index !== -1) {
        if (index < listOfTabbableElements.length - 1) {
          return listOfTabbableElements[index + 1];
        }
      }
    }
  },

  fireMessageEvent(element: HTMLElement, message: string, type: 'SUCCESS' | 'INFO' | 'WARNING' | 'ERROR', timeout?: number): void {
    if (element == null || message == null || message.length === 0 || type == null) {
      return;
    }

    //Set default of 3 seconds
    if (timeout == null) {
      timeout = 3000;
    }

    let event: Event = new Event('pdsMessageEvent', { bubbles: true });

    (event as any).detail = { message, type, timeout };

    //attach all information to the event
    element.dispatchEvent(event);
  },

  /**
   * Returns true if a mobile browser is in use.
   * All mobile browser checking should call here so that we can change the way we detect mobile users
   * as well as identify areas where we do this easily.
   *
   * This method should be used as a last resort! For example, events which need to be
   * called for mobile users in order to provide a good user experience but harm the desktop experience.
   */
  isMobile: function(): boolean {
    return this.window.orientation != null;
  },
  /**
   * Returns true if email is of a valid format.
   * Regex pattern picked up from a site which claims  99.99% verification: http://emailregex.com/
   */
  isValidEmail: function (email: string): boolean {
    return EmailUtil.validateEmail(email);
  },
  /**
   * Gets the number of milliseconds since that date in the 70s.
   * @param date
   * @returns {number}
   */
  dateToTime: function (date: Date): number | undefined {
    //Yes, it's supposed to be a date, but this can be called from outside typescript so we check to make sure
    //getTime is really there before we call it.
    if (date != null && typeof date.getTime === 'function') {
      return date.getTime();
    }
  },
  /**
   * element and selector are required to get this function to work properly.  Passing in undefined to either of them will
   * simply make the function return undefined.  We built it this way because sometimes this function will be called with
   * "optional"
   * Sub for $.closest()
   * @param element
   * @param selector
   * @returns {any}
   */
  getClosest: function (element: HTMLElement | undefined | null, selector: string | undefined): { querySelector: (string) => any } | undefined {
    if (element == null || typeof element.matches !== 'function' || selector == null) {
      return;
    }

    while (element != null) {
      try {
        if (element.matches(selector)) {
          return element;
        }
      } catch (e) {
        // error with matches, may be caused by an invalid selector
        return;
      }

      element = element.parentElement;
    }
  },
  prepend: function(newNode: HTMLElement, referenceNode: HTMLElement): void {
    this.insertBefore(newNode, <HTMLElement>referenceNode.firstChild);
  },
  append: function(newNode: HTMLElement, referenceNode: HTMLElement): void {
    referenceNode.appendChild(newNode);
  },
  insertBefore: function (newNode: HTMLElement, referenceNode: HTMLElement): void {
    if (referenceNode.parentNode != null) {
      referenceNode.parentNode.insertBefore(newNode, referenceNode);
    }
  },
  insertAfter: function (newNode: HTMLElement, referenceNode: HTMLElement): void {
    if (referenceNode.parentNode != null) {
      referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    }
  },
  is: function (element: HTMLElement, selector: string): boolean | undefined {
    if (element == null || typeof element.matches !== 'function' || selector == null) {
      return;
    }

    try {
      return element.matches(selector);
    } catch (e) {
      // error with matches, may be caused by an invalid selector
      return;
    }
  },
  /**
   * Sub for $.addClass()
   * @param element a DOM element.
   * @param className The class name to add.
   */
  addClass: function (element: HTMLElement | null | undefined, className: string): void {
    if (element == null || className == null || className.trim().length === 0) {
      return;
    }
    element.classList.add(className);
  },

  /**
   * Checks to see if an element contains a class...
   * Includes some extra checks that should be done
   * @param element
   */

  containsClass: function (element: HTMLElement | null | undefined, className: string): boolean {
    if (element == null || className == null) {
      return false;
    } else if (className.trim().length === 0) {
      return false;
    } else {
      return element.classList.contains(className);
    }
  },

  /**
   * Sub for $.removeClass()
   * @param element a DOM element
   * @param className The class name to remove.
   */
  removeClass: function (element: HTMLElement | null | undefined, className: string): void {
    if (element == null || className == null || className.trim().length === 0) {
      return;
    }
    element.classList.remove(className);
  },
  /**
   * Stupid IE doesn't toggle classes correctly on a classList object when the force option is passed in
   * so we wrote our own.
   *
   * If you do not pass one or all of the inputs into the method then it will simply return false and not blow up.
   *
   * @param element - The element to toggle the class on.
   * @param className - The class name to toggle on or off.
   * @returns true if the class is present, false if the class is not present.
   */
  toggleClass: function(element: HTMLElement | null | undefined, className: string, force?: boolean): boolean {
    if (element == null || className == null || className.trim().length === 0) {
      return false;
    }

    if (force == null) {
      return element.classList.toggle(className);
    }

    if (force) {
      element.classList.add(className);
    } else {
      element.classList.remove(className);
    }

    return force;
  },
  /**
   * Removes an element from the DOM.  Does not blow up if you pass in undefined.
   * @param element
   */
  remove: function(element: HTMLElement | null | undefined): void {
    if (element != null && element.parentNode != null) {
      element.parentNode.removeChild(element);
    }
  },
  /**
   * Sub for _.flatten()
   * @param arry
   * @returns {Array}
   */
  flatten: function (arry: Array<any>): Array<any> {
    let result: Array<any> = [];
    if (arry != null && arry instanceof Array) {
      arry.forEach((arrayElement: any) => {
        if (arrayElement instanceof Array) {
          this.flatten(arrayElement).forEach((flattenedElement: any) => {
            result.push(flattenedElement);
          });
        } else {
          result.push(arrayElement);
        }
      });
    }
    return result;
  },
  //Given a date, generate all the numbers.
  generateDays: function(year: number, month: number, fillBlankDays = false): Array<CalendarDay> {
    if (typeof year !== 'number' || typeof month !== 'number') {
      return [];
    }

    let dateTimes: Array<CalendarDay> = [];
    //create a new date with the same year and month, but start on the first.
    let dateItr = new Date(year, month, 1);

    //subroutine for adding blanks
    let addBlanks = function (loopTimes: number, computeDates: boolean, ascending: boolean): void {
      let alterDate: Date | undefined;
      if (computeDates) {
        if (ascending) {
          //Start (Loop times) before the first day of the month.
          alterDate = new Date(year, month, -1 * loopTimes + 1);
        } else {
          //Start on First Day of next month
          alterDate = new Date(year, month + 1, 1);
        }
      }

      let sizeModSeven;
      for (let i = 0; i < loopTimes; i++) {
        sizeModSeven = (dateTimes.length) % 7;
        if (computeDates && alterDate != null) {
          dateTimes.push({
            dayNum: alterDate.getDate(),
            dateTimeNum: alterDate.getTime(),
            isWeekend: sizeModSeven === 0 || sizeModSeven === 6,
            isFilled: true,
            dashCaseDate: `${alterDate.getFullYear()}-${('0' + (alterDate.getMonth() + 1)).slice(-2)}-${('0' + alterDate.getDate()).slice(-2)}`
          });
          alterDate.setDate(alterDate.getDate() + 1);
        } else {
          dateTimes.push({
            isWeekend: sizeModSeven === 0 || sizeModSeven === 6,
            isFilled: true
          });
        }
      }
    };
    //Day of week for first day of month => number of blanks.
    addBlanks(dateItr.getDay(), fillBlankDays, true);

    let sizeModSeven;
    do {
      sizeModSeven = (dateTimes.length) % 7;
      dateTimes.push({
        dayNum: dateItr.getDate(),
        dateTimeNum: dateItr.getTime(),
        isWeekend: sizeModSeven === 0 || sizeModSeven === 6,
        dashCaseDate: `${dateItr.getFullYear()}-${('0' + (dateItr.getMonth() + 1)).slice(-2)}-${('0' + dateItr.getDate()).slice(-2)}`
      });
      dateItr.setDate(dateItr.getDate() + 1);
    }
    while (dateItr.getMonth() === month);

    if (dateTimes.length % 7 > 0) {
      addBlanks(7 - (dateTimes.length % 7), fillBlankDays, false);
    }

    return dateTimes;
  },
  getpageYOffsetLimit: function (): number {
    return Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      ) - this.window.innerHeight;
  },
  getUniqueId: function (theId: string): string {
    let curNumber = 1;
    let curId = `${theId}_${curNumber}`;
    if (document.getElementById(theId) == null) {
      return theId;
    }
    while (document.getElementById(curId) != null) {
      curNumber += 1;
      curId = `${theId}_${curNumber}`;
    }
    return curId;
  },
  sort: function (array: Array<any>, valueExtractor: (value: any) => any): void {
    if (array == null || array.length === 0) {
      return;
    }

    array.sort(function (a, b): number {
      let varA = valueExtractor(a);
      let varB = valueExtractor(b);

      if (varA < varB) {
        return -1;
      } else if (varA > varB) {
        return 1;
      } else {
        return 0;
      }
    });
  },
  TOKEN_REGEX: /{[^}]+}/g,  // examples: {something}, {somethingElse}, etc
  /**
   * returns a string with tokens replaced by values.
   * @param theString - the string that context is applied to. Any tokens in the string will be replaced by
   * the proper values from the context parameter. A token is defined as a word surrounded by {}.
   * Example theString value: 'Hi my name is {name}.'
   * @param context - the object that has the context to apply to the string. If it is a string there should only
   * be one token in theString.
   * note: tokens are case-insensitive. So, {someThing} is the same as {something}.
   * Examples: {name:'shane'}, 'shane'
   * @returns {string} - theString with context applied to it.
   * Example using example parameters: 'Hi my name is shane'
   */
  replaceTokensInString: function (theString: string, context: string | Object): string {
    if (theString == null || context == null || theString === '') {  //handle null strings
      return theString;
    }
    if (typeof context === 'string') {
      let allTokens = theString.match(this.TOKEN_REGEX);
      if (allTokens == null) {  // if there are no tokens we can just return theString
        return theString;
      }
      let valid = allTokens.every((_token) => {  // if context is a string there should only be one distinct token
        // the ! is needed here because typescript doesn't pick up on the null check's
        // semantics and thinks allTokens could be null
        return _token === allTokens![0];
      });
      if (!valid) {
        throw 'replaceTokensInString called with invalid parameters.';
      }
      return theString.replace(this.TOKEN_REGEX, context);
    }

    for (let key in context) {  // context must be an object so loop through it's keys and apply their values to theString
      if (context.hasOwnProperty(key)) {
        let re = new RegExp(`{${key}}`, 'ig');
        theString = theString.replace(re, context[key]);
      }
    }
    return theString;
  },
  getUrlSearchParams: function (): { [key: string]: string } {
    let params = {};

    let search = this.window.location.search;

    if (search != null && search.length > 1) {
      let parts;
      //remove the ?
      search = search.substring(1);
      for (let token of search.split('&')) {
        parts = token.split('=');
        if (parts != null && parts.length > 0) {
          params[parts[0]] = parts[1];
        }
      }
    }
    return params;
  },
  /**
   * If one of your url params contains an object in JSON notation, run it through this to parse it.
   * Is great for calling after getUrlSearchParams.
   */
  parseUrlSearchObjectParam: function(param: string, paramEncoded = true): Object {
    try {
      let paramString = unescape(param);
      if (paramEncoded) {
        paramString = decodeURIComponent(atob(paramString));
        // paramString = atob(paramString);
      }
      let parsedParam = JSON.parse(paramString);
      return parsedParam;
    } catch (err) {
      //Default to just returning an empty object.
      return {};
    }
  },
  stringifyUrlSearchObjectParam: function (param: Object, encodeParam = true): string {
    let p: string = JSON.stringify(param);
    if (encodeParam) {
      try {
        p = btoa(encodeURIComponent(p));
        // p = btoa(p);
      } catch (err) {
        console.error('could not base 64 encode string: ' + p);
        p = btoa(JSON.stringify({}));
      }
    }
    return p;
  },
  removeContextMarkerFromUrl: function (url: string): string {
    let ucCtx = 'ucCtx=\{unifiedClassroomContext\}';
    return url.replace(new RegExp('&' + ucCtx), '')
      .replace(new RegExp('\\?' + ucCtx + '&'), '?')
      .replace(new RegExp('\\?' + ucCtx), '');
  },
  getSectionColorForClass: function (sectionOrGroup: any): string {
    let colorNumber = 1;
    if (sectionOrGroup == null) {
      return 'color-' + colorNumber;
    }
    if (sectionOrGroup && sectionOrGroup.sourceSystemId != null && sectionOrGroup.courseNumber != null) {
      if (isNaN(parseInt(sectionOrGroup.courseNumber))) {
        colorNumber = (Number(sectionOrGroup.sourceSystemId) % 4) + 1;
      } else {
        colorNumber = (parseInt(sectionOrGroup.courseNumber) % 4) + 1;
      }
    } else if (sectionOrGroup && sectionOrGroup.sectionUids != null) {
      colorNumber = (sectionOrGroup.sectionUids.length % 4) + 1;
    }
    return 'color-' + colorNumber;
  },
  /**
   * fixTimeStringSpacing
   * @param timeString - a string that can be validated with pdsi18n.timeRegexString
   * @returns {string} - a string that has spacing between time and am/pm so that the following parses correctly:
   *  new Date('1/1/2001 ' + fixTimeStringSpacing(someTimeString));
   */
  fixTimeStringSpacing: function (timeString: string): string {
    if (timeString != null) {
      return timeString.replace(this.TIME_STRING_AM_PM_SPACING_REGEX, '$1 $2');
    } else {
      return timeString;
    }
  },

  /**
   * Performs an asynchronous HTTP (Ajax) request.
   * @param url - A string containing the URL to which the request is sent.
   * @param settings - A set of key/value pairs that configure the Ajax request. All settings are optional. See AjaxSettings interface for more details.
   */
  ajax: function (url: string, settings?: AjaxSettings): XMLHttpRequest | undefined {
    // If no settings are passed in, we want to create an empty settings object to populate.
    if (settings == null) {
      settings = {};
    }
    // Default settings required to make a request
    let async = settings.async || true;
    let method = settings.method || 'GET';
    let headers = settings.headers || {};
    let xhrFields = settings.xhrFields || {};
    let data = settings.data;
    let success = settings.success;
    let error = settings.error;
    let complete = settings.complete;
    let xhr = settings.xhr ? settings.xhr() : new XMLHttpRequest();
    let disableAjaxStatus = settings.disableAjaxStatus || false;
    try {
      xhr.open(
        method,
        url,
        async
      );
      if (!disableAjaxStatus) {
        fireAjaxStatus({ status: 'START', url, payload: data });
      }
      for (let key in xhrFields) {
        if (xhrFields.hasOwnProperty(key)) {
          xhr[key] = xhrFields[key];
        }
      }
      for (let key in headers) {
        if (headers.hasOwnProperty(key)) {
          xhr.setRequestHeader(key, headers[key]);
        }
      }
      xhr.onreadystatechange = function (): void {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          if (xhr.status === 200 || xhr.status === 304) {
            if (success != null) {
              success(xhr.response, xhr, xhr.statusText);
              if (!disableAjaxStatus) {
                fireAjaxStatus({ status: 'SUCCESS', url, payload: data });
              }
            }
          } else {
            if (error != null) {
              error(xhr, xhr.statusText);
              if (!disableAjaxStatus) {
                fireAjaxStatus({ status: 'ERROR', url, payload: data });
              }
            }
          }
          if (complete != null) {
            complete(xhr, xhr.statusText);
            if (!disableAjaxStatus) {
              fireAjaxStatus({ status: 'COMPLETE', url, payload: data });
            }
          }
        }
      };
      xhr.send(data);
      return xhr;
    } catch (e) {
      console.error(e);
      if (!disableAjaxStatus) {
        fireAjaxStatus({ status: 'ERROR', url, payload: data });
      }
    }
  },
  /**
   * This function returns XHR Request behavior subject as an Observable
   * to prevent callers from triggering the next() function on behavior subject.
   */
  getAjaxStatusStream: function (): Observable<AjaxStatus> {
    return ajaxStatusStream.asObservable();
  },
  hasActiveAjaxRequests: function(): boolean {
    return ajaxActiveRequestsCount > 0;
  },
  /**
   * This function takes the constructor generated by
   * typescript, and returns a new object instance that has all of the properties and functions all defined
   * as non-inherited properties of a new instance of the passed in constructor.
   *
   * Note for polymer:
   * Polymer needs an object definition with all properties on the same object with no inheritance tree. It
   * also needs an actual object, not an object constructor. Using TypeScript to create Polymer definitions
   * is super convenient because it allows us to take advantage of inheritance, but it gives us back an object
   * that is a constructor, and has an inheritance tree.
   */
  createFlattenedClassInstance: function (token: any): any {
    let tsObject = new token();
    let definition = {};
    let objectDef;
    /* tslint:disable */
    for (let prop in tsObject) {
      /* tslint:enable */
      objectDef = getPropertyDescriptor(tsObject, prop);
      if (objectDef != null && objectDef.hasOwnProperty('get')) {
        Object.defineProperty(definition, prop, objectDef);
      } else {
        definition[prop] = tsObject[prop];
      }
    }
    return definition;
  },
  /* groupBy is like lodash's groupBy but without expression parsing - not in common-utils because backend uses lodash
   https://lodash.com/docs/4.17.4#groupBy
   */
  groupBy(items: Array<any>, key: (item) => string): { [key: string]: any } {
    let groups = {};

    for (let item of items) {
      if (groups[key(item)] == null) {
        groups[key(item)] = [item];
      } else {
        groups[key(item)].push(item);
      }
    }

    return groups;
  },
  /**
   * Gets element that currently has focus. document.activeElement should work for most browsers, but in case not, we try using a querySelector.
   * @returns {HTMLElement} The HTMLElement that has the focus.
   */
  getElementInFocus(): HTMLElement {

    let elementInFocus = (
      document.hasFocus() &&
      document.activeElement !== document.body &&
      document.activeElement !== document.documentElement &&
      document.activeElement
    ) as HTMLElement || null;

    elementInFocus = elementInFocus || document.querySelector(':focus') as HTMLElement;
    return elementInFocus;
  },
  /**
   * Takes in a DOM node and grabs an element with the given query.
   * If the element is found we focus on it and return true, otherwise we return false.
   * @param context The DOM node we want to query
   * @param query The query used to search the DOM node
   */
  focus(query: string, context: Element | Document): boolean {
    let element = (<HTMLElement>context.querySelector(query));
    if (element != null) {
      element.focus();
      return true;
    } else {
      return false;
    }
  },
  /**
   * Throws the exception, but first prints the actual stack trace because Angular prints non useful stack traces.
   */
  throw: function(errorMessage: string): void {
    // tslint:disable-next-line
    console.trace(errorMessage);
    throw errorMessage;
  },
  roundNumber(value: number, precision: number): number {
    return NumberUtil.round(value, precision);
  },
  getStringBooleanValue(value: StringBoolean): boolean {
    return value && value.toLowerCase() === 'true' || false;
  },
  /**
   * Build a download anchor element that can be inserted into the DOM.
   *
   * @param params
   */
  buildDownloadAnchor(params: DownloadFileParams): any {
    // Create the download anchor (including data URI) and put it into the div
    let downloadAnchor = document.createElement('a');
    // The correct way to create a data URI that should work properly and without
    // data size limits is to use a blob and then use window.URL.createObjectURL()
    // to build the href from the blob.
    downloadAnchor.href = window.URL.createObjectURL(new Blob([params.content],
      {type: `${params.mediaType != null ? params.mediaType : DownloadFileMediaType.TEXT_PLAIN}`}));
    // Set the download file name attribute
    downloadAnchor.setAttribute('download', (params.downloadFileName != null ?
      params.downloadFileName : pdsUtils.DEFAULT_DOWNLOAD_FILENAME)
      + '.' + TypeExtensionMap.get(computeMediaType(params.mediaType)));
    return downloadAnchor;
  },
  /**
   * Utility function triggers a download of the provided content as a
   * file specifically in the IE browser.
   *
   * @param params
   */
  startBrowserSaveStringAsFileIE(params: DownloadFileParams): void {
    // Invoke save of content as blob in IE
    window.navigator.msSaveOrOpenBlob(new Blob([params.content],
      {type: `${params.mediaType != null ? params.mediaType : DownloadFileMediaType.TEXT_PLAIN}`}),
      (params.downloadFileName != null ? params.downloadFileName : pdsUtils.DEFAULT_DOWNLOAD_FILENAME)
      + '.' + TypeExtensionMap.get(computeMediaType(params.mediaType)));
  },
  /**
   * This utility function triggers a download of the provided content as a
   * file in the browser.
   *
   * @param params Parameters for the process, including content
   */
  startBrowserSaveStringAsFile(params: DownloadFileParams): void {
    // Check whether browser is IE
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // Handle an IE scenario for downloading a blob as a file
      pdsUtils.startBrowserSaveStringAsFileIE(params);
    } else {
      // This path is for downloading a blob as a file for all other supported browsers
      // First, build download anchor
      let downloadAnchor = pdsUtils.buildDownloadAnchor(params);
      // Then, make anchor hidden
      downloadAnchor.setAttribute('style', 'display:none;');
      // Now put the download anchor div into the DOM
      document.body.appendChild(downloadAnchor);
      // Trigger the download in the browser
      downloadAnchor.click();
      // Delete download link after download
      pdsUtils.remove(downloadAnchor);
    }
  },
  /**
   * Default name to use for download file, in case one a name is not provided
   * in the params object by code calling downloadFile function.
   */
  DEFAULT_DOWNLOAD_FILENAME: 'Download',
  // translate a Css value to px so that we can compare normalized values
  getCssValueInPx(style: string | null): number {
    if (style == null) {
      return NaN;
    }

    // matches <number><unit>
    let match = style.match(/^(\d+\.?\d*)([^\d]+)$/);
    if (match == null) {
      throw new Error(`Unrecognized style value: ${style}`);
    }

    let units = match[2];
    let value;
    switch (units) {
      case 'px':
        value = parseFloat(style);
        break;
      case 'pt':
        // 1 pt is typically 4/3 of 1 px
        //  1em = 12pt = 16px = 100%
        //  https://kyleschaeffer.com/css-font-size-em-vs-px-vs-pt-vs-percent
        value = parseFloat(style) * (4 / 3);
        break;
      default:
        throw new Error(`Unrecognized unit: ${units}`);
    }

    return value;
  },
  // get the bounding box for a text node
  getTextNodeBoundingBox(textNode: Node): BoundingBox {
    // text node positioning can be found by create a range, selecting the text, then combining each line's positioning
    let range = document.createRange();
    range.selectNodeContents(textNode);
    let rects = range.getClientRects();

    if (rects.length === 0) {
      throw new Error('The node does not have any text!');
    }

    // initialize from the first line of text
    let left = rects[0].left;
    let top = rects[0].top;
    let right = rects[0].right;
    let bottom = rects[0].bottom;

    // go through each line of text and expand the bounding box to encompass the new line of text
    for (let i = 1; i < rects.length; i++) {
      let rect = rects[i];

      left = Math.min(rect.left, left);
      top = Math.min(rect.top, top);
      right = Math.max(rect.right, right);
      bottom = Math.max(rect.bottom, bottom);
    }

    // the position so far is offset from the screen - change that to page offset
    let leftPageOffset = window.pageXOffset - (document.documentElement.clientLeft || 0);
    let topPageOffset = window.pageYOffset - (document.documentElement.clientTop || 0);

    left += leftPageOffset;
    right += leftPageOffset;
    top += topPageOffset;
    bottom += topPageOffset;

    // return the page offset values
    return { left, top, right, bottom };
  },
  // gets the element offset
  getOffset(element: HTMLElement, includeMargins = false): Offset {
    let rect = element.getBoundingClientRect();

    // the offset so far is from the screen - change that to page offset
    let left = rect.left + window.pageXOffset - (document.documentElement.clientLeft || 0);
    let top = rect.top + window.pageYOffset - (document.documentElement.clientTop || 0);

    // if we're including margins, in the offset, we need to grab the px value and correct the position
    if (includeMargins) {
      let style = getComputedStyle(element);

      if (style.marginLeft && style.marginLeft !== '0px') {
        let marginLeft = pdsUtils.getCssValueInPx(style.marginLeft);
        left -= marginLeft;
      }

      if (style.marginTop && style.marginTop !== '0px') {
        let marginTop = pdsUtils.getCssValueInPx(style.marginTop);
        top -= marginTop;
      }
    }

    return { left, top };
  },
  // gets a bounding box that represents the offset from a container along with the length and width of the box
  getTextNodeBoundingBoxOffsetFromContainer(container: HTMLElement, node: Node): BoundingBox {
    // start with the bounding box for the node, positioned based on the page
    let box = pdsUtils.getTextNodeBoundingBox(node);
    let left = box.left;
    let top = box.top;

    let width = box.right - box.left;
    let height = box.bottom - box.top;

    // then get the offset for the container, positioned based on the page
    let containerOffset = pdsUtils.getOffset(container);

    // use the difference between the two to determine the container -> node offset
    left -= containerOffset.left;
    top -= containerOffset.top;

    // determine the new right/bottom points based on the original width and height
    let right = left + width;
    let bottom = top + height;

    return { left, top, right, bottom };
  },
  // gets a bounding box that represents the offset from a container along with the length and width of the box
  getBoundingBoxOffsetFromContainer(container: HTMLElement, element: HTMLElement, includeMargins = false): BoundingBox {
    // start with the offset for the element, positioned based on the page
    let offset = pdsUtils.getOffset(element, includeMargins);
    let left = offset.left;
    let top = offset.top;

    let width = element.offsetWidth;
    let height = element.offsetHeight;

    // if we're including margins, process the margin styles and add them as appropriate
    if (includeMargins) {
      let style = getComputedStyle(element);

      if (style.marginLeft && style.marginLeft !== '0px') {
        let marginLeft = pdsUtils.getCssValueInPx(style.marginLeft);
        width += marginLeft;
      }

      if (style.marginTop && style.marginTop !== '0px') {
        let marginTop = pdsUtils.getCssValueInPx(style.marginTop);
        height += marginTop;
      }

      if (style.marginRight && style.marginRight !== '0px') {
        width += pdsUtils.getCssValueInPx(style.marginRight);
      }

      if (style.marginBottom && style.marginBottom !== '0px') {
        height += pdsUtils.getCssValueInPx(style.marginBottom);
      }
    }

    // then get the offset for the container, positioned based on the page
    let containerOffset = pdsUtils.getOffset(container);

    // use the difference between the two to determine the container -> element offset
    left -= containerOffset.left;
    top -= containerOffset.top;

    // determine the new right/bottom points based on the original width and height
    let right = left + width;
    let bottom = top + height;

    return { left, top, right, bottom };
  },
  /**
   * Returns true if the css display property is inline, inline-block, inline-flex, or any other display values with inline in the name.
   * @param element
   * @returns {boolean}
   */
  isInline(element: HTMLElement): boolean {
    let style = getComputedStyle(element);
    return style.display == null || style.display.includes('inline');
  }
};

/**
 * Compute the media type.
 *
 * @param mediaType
 */
function computeMediaType(mediaType?: DownloadFileMediaType): DownloadFileMediaType {
  // Was media type not specified?
  if (mediaType == null) {
    return DownloadFileMediaType.TEXT_PLAIN;
  }
  return mediaType;
}

/**
 * Helper function that tracks the number of active ajax requests and fires ajax status event.
 * @param ajaxStatus
 */
function fireAjaxStatus(ajaxStatus: AjaxStatus): void {
  if (ajaxStatus) {
    switch (ajaxStatus.status) {
      case 'START':
        ajaxActiveRequestsCount++;
        break;

      case 'SUCCESS':
      case 'ERROR':
      case 'COMPLETE':
      default:
        if (ajaxActiveRequestsCount > 0) {
          ajaxActiveRequestsCount--;
        }
        break;
    }
    ajaxStatusStream.next(ajaxStatus);
  }
}

//Polyfill for .matches function. Gives IE compatibility.
//I found this on Stack Overflow. Seems to work...
if (typeof Element !== 'undefined' && !Element.prototype.matches) {
  Element.prototype.matches =
    (<any>Element.prototype).matchesSelector ||
    (<any>Element.prototype).mozMatchesSelector ||
    (<any>Element.prototype).msMatchesSelector ||
    (<any>Element.prototype).oMatchesSelector ||
    (<any>Element.prototype).webkitMatchesSelector ||
    function (s: any): boolean {
      let matches = (this.document || this.ownerDocument).querySelectorAll(s),
        i = matches.length;
      while (--i >= 0 && matches.item(i) !== this) {
      }
      return i > -1;
    };
}
//Polyfill for Date.pdsToDateWidgetAttr method. This will be used for
//validation attributes in PdsDateWidget.
if (!(<any>Date).prototype.pdsToDateWidgetAttr) {
  (<any>Date).prototype.pdsToDateWidgetAttr = function (): string {
    return (this.getMonth() + 1) + '/' + this.getDate() + '/' + this.getFullYear();
  };
}
